import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { environment } from '../config/environment';

interface LateJoinCardProps {
  gamePin: string;
  isOpen: boolean;
  onClose: () => void;
  lateJoinEnabled: boolean;
  onToggleLateJoin: () => void;
}

const LateJoinCard: React.FC<LateJoinCardProps> = ({
  gamePin,
  isOpen,
  onClose,
  lateJoinEnabled,
  onToggleLateJoin
}) => {
  const playerUrl = `${environment.playerUrl}/${gamePin}`;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Card */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md animate-scale-in">
        <div className="bg-white rounded-lg shadow-2xl border-2 border-gray-300 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-4 flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">Join Game</h3>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Late Join Toggle */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Allow Late Join</p>
                  <p className="text-sm text-gray-600">Players can join after game starts</p>
                </div>
                <button
                  onClick={onToggleLateJoin}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    lateJoinEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      lateJoinEnabled ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {lateJoinEnabled ? (
              <>
                {/* QR Code */}
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2 font-semibold">Scan QR Code</p>
                  <div className="flex justify-center">
                    <div className="bg-white p-3 rounded-lg border-2 border-orange-400">
                      <QRCodeSVG
                        value={playerUrl}
                        size={150}
                        level="H"
                        includeMargin={false}
                      />
                    </div>
                  </div>
                </div>

                {/* Game PIN */}
                <div>
                  <p className="text-xs text-gray-600 uppercase font-semibold mb-1">Game PIN</p>
                  <div className="bg-gradient-to-r from-orange-100 to-pink-100 rounded-lg px-6 py-3 border-2 border-orange-400">
                    <p className="text-4xl font-black text-orange-600 tracking-wider text-center">
                      {gamePin}
                    </p>
                  </div>
                </div>

                {/* URL */}
                <div>
                  <p className="text-xs text-gray-600 uppercase font-semibold mb-1">Join URL</p>
                  <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
                    <p className="text-sm font-mono text-gray-800 break-all text-center">
                      {playerUrl}
                    </p>
                  </div>
                </div>

                {/* Copy Button */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(playerUrl);
                  }}
                  className="w-full px-4 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-md hover:shadow-lg"
                >
                  📋 Copy Join Link
                </button>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🔒</div>
                <p className="text-gray-600">Late joining is currently disabled</p>
                <p className="text-sm text-gray-500 mt-2">Enable the toggle above to allow players to join</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LateJoinCard;
